/**
 * Pure filter / sort helpers for the Users Management list.
 *
 * Shared by {@link useUsersList} (what the table shows) and usersExport (what
 * the CSV contains) so the two can never disagree about which rows match a
 * filter or what a row's Status reads.
 */

import type { UserProfile } from '../../lib/api';
import { DEPARTMENTS, departmentKey, departmentLabel } from '../../lib/departments';
import type { DepartmentValue } from '../../lib/departments';
import type { SortField, SortDirection } from './UserTable';

/**
 * Mirrors the Status column's badges so the filter can select anything the
 * column can show. It previously offered only active/inactive, which left
 * "Invite Sent" — the one state an admin actually chases — unfilterable.
 */
export type StatusFilter = 'all' | 'active' | 'inactive' | 'invited';
export type ApprovalFilter = 'all' | 'approved' | 'pending';
/** A department value, 'none' for a blank department, or 'all'. */
export type DepartmentFilter = 'all' | 'none' | DepartmentValue;
export type UserTypeTab = 'customer' | 'supplier' | 'valuechain';

/** True when the row renders the amber "Invite Sent" badge. Kept beside the
 *  filter so the two cannot drift from UserTable's badge logic. */
export const isAwaitingInvite = (u: UserProfile): boolean =>
  Boolean(u.user.is_active && u.user.invitation_sent && !u.user.has_joined && !u.user.last_login);

/**
 * The word the Status column shows for a row.
 *
 * @param u - The user row
 * @returns 'Inactive', 'Invite Sent' or 'Active', exactly as the badge reads
 */
export function userStatusLabel(u: UserProfile): string {
  if (!u.user.is_active) return 'Inactive';
  return isAwaitingInvite(u) ? 'Invite Sent' : 'Active';
}

/**
 * Whether a row matches the Department filter.
 *
 * Stored values and labels both match (departmentKey), so a legacy
 * "Purchasing" or an imported "Procurement" row matches Procurement. A department outside the
 * list matches only "All departments".
 *
 * @param u - The user row
 * @param filter - The selected department filter
 * @returns true when the row should stay in the list
 */
export function matchesDepartment(u: UserProfile, filter: DepartmentFilter): boolean {
  if (filter === 'all') return true;
  const key = departmentKey(u.department);
  if (filter === 'none') return key === '';
  return key === filter;
}

/** Department filter options in display order, for the filter Select. */
export const DEPARTMENT_FILTER_OPTIONS: { value: DepartmentFilter; label: string }[] = [
  { value: 'all', label: 'All departments' },
  ...DEPARTMENTS.map((d) => ({ value: d.value as DepartmentFilter, label: d.label })),
  { value: 'none', label: 'No department' },
];

/**
 * Which tab a row belongs to. CR-5367: three-way split — supplier, valuechain
 * (internal Helix), or customer (everything else, e.g. customer + logistics).
 *
 * @param u - The user row
 * @param tab - The active tab
 * @param isHelixViewer - CR-5374 defence-in-depth: an external viewer never
 *   sees Helix users, even on a deep-linked ?tab=valuechain
 * @returns true when the row belongs on that tab for this viewer
 */
export function matchesTab(u: UserProfile, tab: UserTypeTab, isHelixViewer: boolean): boolean {
  if (tab === 'valuechain' && !isHelixViewer) return false;
  if (tab === 'supplier') return u.company_type === 'supplier';
  if (tab === 'valuechain') return u.company_type === 'helix';
  return u.company_type !== 'supplier' && u.company_type !== 'helix';
}

/**
 * Comparator for the sortable columns.
 *
 * @param field - Column being sorted
 * @param direction - asc or desc
 * @returns An Array.sort comparator
 */
export function compareUsers(field: SortField, direction: SortDirection) {
  const dir = direction === 'asc' ? 1 : -1;
  return (a: UserProfile, b: UserProfile): number => {
    let aVal: string | number;
    let bVal: string | number;
    switch (field) {
      case 'last_login':
        aVal = a.user.last_login ? new Date(a.user.last_login).getTime() : 0;
        bVal = b.user.last_login ? new Date(b.user.last_login).getTime() : 0;
        break;
      case 'name': aVal = a.user.full_name; bVal = b.user.full_name; break;
      case 'email': aVal = a.user.email; bVal = b.user.email; break;
      case 'company': aVal = a.company_name; bVal = b.company_name; break;
      // Sort by what the column shows (Procurement, not "purchasing").
      case 'department':
        aVal = departmentLabel(a.department).toLowerCase();
        bVal = departmentLabel(b.department).toLowerCase();
        break;
      case 'role': aVal = a.role_name; bVal = b.role_name; break;
      case 'created_at':
        aVal = new Date(a.created_at).getTime();
        bVal = new Date(b.created_at).getTime();
        break;
      default: return 0;
    }
    if (aVal < bVal) return -dir;
    if (aVal > bVal) return dir;
    return 0;
  };
}
